import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Flag, MapPin, Target } from "lucide-react";
import { Card, SectionLabel } from "@/components/ui/Card";
import { fmtDate } from "@/lib/dates";
import { cn } from "@/lib/utils";
import type { UpcomingEvent } from "@/types";

const PRIORITY_CLS: Record<string, string> = {
  A: "bg-coral/15 text-coral-dark border-coral/30",
  B: "bg-amber/15 text-amber-dark border-amber/30",
  C: "bg-cream-border text-taupe border-cream-border",
};

export function EventsWidget({ events }: { events: UpcomingEvent[] }) {
  const { t } = useTranslation();
  return (
    <Card>
      <div className="flex items-center justify-between">
        <SectionLabel>{t("dashboard.upcoming_races")}</SectionLabel>
        <Link
          to="/settings#events"
          className="text-[11px] font-medium text-amber-dark hover:underline"
        >
          {t("events.manage")}
        </Link>
      </div>

      {events.length === 0 ? (
        <div className="mt-3 rounded-lg border border-dashed border-cream-border bg-cream-light/50 p-4 text-center">
          <Flag className="mx-auto mb-2 h-5 w-5 text-taupe/60" strokeWidth={1.6} />
          <p className="text-[12px] text-taupe">{t("events.empty_title")}</p>
          <Link
            to="/settings#events"
            className="mt-2 inline-block text-[12px] font-medium text-amber-dark hover:underline"
          >
            {t("events.add_race")}
          </Link>
        </div>
      ) : (
        <ul className="mt-3 space-y-2">
          {events.map((e) => (
            <EventCountdownCard key={e.id} event={e} />
          ))}
        </ul>
      )}
    </Card>
  );
}

function EventCountdownCard({ event }: { event: UpcomingEvent }) {
  const { t } = useTranslation();
  const pcls = PRIORITY_CLS[event.priority] || PRIORITY_CLS.C;
  return (
    <li className="rounded-lg border border-cream-border bg-cream p-3">
      <div className="flex items-start gap-3">
        <div className="flex w-10 shrink-0 flex-col items-center rounded-md border border-cream-border bg-cream-light px-1 py-1">
          <span className="text-[9px] uppercase tracking-wider text-taupe">
            {fmtDate(event.date, "MMM")}
          </span>
          <span className="metric-number text-[18px] text-navy-deep">
            {fmtDate(event.date, "d")}
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-0.5 flex flex-wrap items-center gap-1.5">
            <span
              className={cn(
                "rounded border px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider",
                pcls,
              )}
            >
              {event.priority}-race
            </span>
            <span className="text-[10px] text-taupe">{event.event_type_label}</span>
          </div>
          <div className="truncate text-[13px] font-medium text-navy-deep">
            {event.name}
          </div>
          {event.location ? (
            <div className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-taupe">
              <MapPin className="h-3 w-3" strokeWidth={1.8} />
              {event.location}
            </div>
          ) : null}
        </div>
      </div>

      <div className="mt-2.5 flex items-center justify-between border-t border-cream-border pt-2">
        <span className="text-[11px] text-taupe">{t("events.left")}</span>
        <span className="metric-number text-[16px] text-navy-deep">
          {event.days_until}
          <span className="ml-1 text-[11px] text-taupe">{t("events.days_short")}</span>
        </span>
      </div>

      {event.goal_time_formatted ? (
        <div className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-taupe">
          <Target className="h-3 w-3 text-amber-dark" strokeWidth={1.8} />
          {t("events.goal")}{" "}
          <span className="metric-number text-navy-deep">
            {event.goal_time_formatted}
          </span>
        </div>
      ) : null}
    </li>
  );
}
